/**
 * Loader Manager
 * Handles skeleton loading UI while iframe is initializing
 */

import { ELEMENT_IDS } from '../constants/sdk-constants';
import {
  getLoaderColorScheme,
  getShimmerGradient,
  getLoaderContainerStyles,
  getSkeletonCardStyles,
  getSkeletonMethodRowStyles,
  getSkeletonBarStyles,
  getButtonSkeletonStyles,
  SHIMMER_ANIMATION,
} from './styles';

export interface LoaderConfig {
  fillParent?: boolean;
  /** From `appearance.variables.loaderColor`. */
  loaderColor?: string;
}

export class LoaderManager {
  private container: HTMLElement;
  private config: LoaderConfig;
  private loaderElement: HTMLElement | null = null;

  constructor(container: HTMLElement, config: LoaderConfig = {}) {
    this.container = container;
    this.config = config;
  }

  /**
   * Show skeleton loader
   */
  show(iframe?: HTMLIFrameElement): void {
    // Hide iframe while loader is showing
    if (iframe) {
      iframe.style.display = 'none';
    }

    const colors = getLoaderColorScheme(this.config.loaderColor);
    const shimmerGradient = getShimmerGradient(colors);

    const loader = document.createElement('div');
    loader.id = ELEMENT_IDS.LOADER;
    loader.style.cssText = getLoaderContainerStyles();

    const skeletonCard = document.createElement('div');
    skeletonCard.style.cssText = getSkeletonCardStyles(
      this.config.fillParent || false,
    );

    // Three horizontal method placeholders
    const methodRow = document.createElement('div');
    methodRow.style.cssText = getSkeletonMethodRowStyles();

    for (let i = 0; i < 3; i++) {
      const methodBox = document.createElement('div');
      methodBox.className = 'inflowpay-skeleton';
      methodBox.style.cssText = getSkeletonBarStyles(shimmerGradient, {
        height: 52,
        radius: 12,
        grow: true,
      });
      methodRow.appendChild(methodBox);
    }

    // Single long field bar
    const fieldBar = document.createElement('div');
    fieldBar.className = 'inflowpay-skeleton';
    fieldBar.style.cssText = getSkeletonBarStyles(shimmerGradient, {
      height: 48,
      radius: 10,
    });

    // Pay button
    const buttonSkeleton = document.createElement('div');
    buttonSkeleton.className = 'inflowpay-skeleton';
    buttonSkeleton.style.cssText = getButtonSkeletonStyles(shimmerGradient);

    skeletonCard.appendChild(methodRow);
    skeletonCard.appendChild(fieldBar);
    skeletonCard.appendChild(buttonSkeleton);

    loader.appendChild(skeletonCard);

    this.injectShimmerStyles();

    this.container.appendChild(loader);
    this.loaderElement = loader;
  }

  /**
   * Hide loader
   */
  hide(iframe?: HTMLIFrameElement): void {
    const loader = document.getElementById(ELEMENT_IDS.LOADER);
    if (loader) {
      loader.remove();
    }
    this.loaderElement = null;

    if (iframe) {
      iframe.style.display = '';
    }
  }

  /**
   * Inject shimmer animation styles into document head
   */
  private injectShimmerStyles(): void {
    if (!document.getElementById(ELEMENT_IDS.LOADER_STYLES)) {
      const style = document.createElement('style');
      style.id = ELEMENT_IDS.LOADER_STYLES;
      style.textContent = SHIMMER_ANIMATION;
      document.head.appendChild(style);
    }
  }
}
